import { useId, useState } from 'react'
import { useIntl } from 'react-intl'

import { SUPPORT_TITLE_MAX_LENGTH } from '@onr/schema'
import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'
import { PencilIcon } from 'lucide-react'

import { Button } from '@/components/atoms/Button/Button.tsx'
import { InlineError } from '@/components/atoms/InlineError'
import { Input } from '@/components/atoms/Input/Input.tsx'

export interface SupportAgentTitleProps {
  readonly id: string
  readonly title: string
  readonly saving?: boolean
  readonly error?: boolean
  readonly onRename?: (title: string) => Promise<boolean>
}

export const SupportAgentTitle = ({ id, title, saving = false, error = false, onRename }: SupportAgentTitleProps) => {
  const intl = useIntl()
  const inputId = useId()
  const [draft, setDraft] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  if (draft === null || !onRename) {
    return (
      <div className="flex flex-wrap items-start gap-2">
        <h2 id={id} className="font-heading flex-[1_1_280px] text-lg font-semibold text-gray-900">
          {title}
        </h2>
        {onRename && (
          <button
            type="button"
            onClick={() => {
              setDraft(title)
              setSubmitted(false)
            }}
            className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-[4px] px-2 text-sm text-blue-700 hover:text-blue-600">
            <PencilIcon aria-hidden className="size-3.5" />
            {intl.formatMessage({ id: 'soutien.agent.rename' })}
          </button>
        )}
      </div>
    )
  }

  const trimmed = draft.trim()
  const save = async () => {
    if (trimmed === '' || saving) {
      return
    }
    setSubmitted(true)
    if (trimmed === title || (await onRename(trimmed))) {
      setDraft(null)
    }
  }

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        handleNotAwaitedPromise(save())
      }}>
      <h2 id={id} className="sr-only">
        {title}
      </h2>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex min-w-0 flex-[1_1_200px] flex-col gap-1">
          <label htmlFor={inputId} className="text-sm font-bold text-gray-800">
            {intl.formatMessage({ id: 'soutien.agent.rename.label' })}
          </label>
          <Input
            id={inputId}
            value={draft}
            maxLength={SUPPORT_TITLE_MAX_LENGTH}
            autoFocus
            className="h-9 border-2 border-blue-700 text-base text-gray-900"
            onChange={(event) => {
              setDraft(event.target.value)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setDraft(null)
              }
            }}
          />
        </div>
        <Button
          className="h-9 border border-gray-300 bg-white px-3.5 text-base font-normal text-gray-700 hover:bg-gray-50 active:bg-gray-100"
          onClick={() => {
            setDraft(null)
          }}>
          {intl.formatMessage({ id: 'soutien.agent.rename.cancel' })}
        </Button>
        <Button type="submit" variant="primary" className="h-9 px-3.5" loading={saving} disabled={trimmed === ''}>
          {intl.formatMessage({ id: 'soutien.agent.rename.save' })}
        </Button>
      </div>
      {error && submitted && <InlineError message={intl.formatMessage({ id: 'soutien.agent.rename.error' })} />}
    </form>
  )
}
